import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    query,
    where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import moment from "moment";

// MUI
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Damagenotes() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
    });

    const [damages, setDamages] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [propertyId, setPropertyId] = useState(null);

    /* ---------------- AUTH + PROPERTY ---------------- */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            const userRef = doc(db, "authroles", user.uid);
            const snap = await getDoc(userRef);

            if (snap.exists()) {
                setPropertyId(snap.data().propertyId);
            }
        });

        return () => unsub();
    }, []);

    /* ---------------- FETCH DAMAGE NOTES ---------------- */
    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, "damages"),
            where("propertyId", "==", propertyId),
            orderBy("createdAt", "desc")
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setDamages(list);
            setFiltered(list);
        });

        return () => unsub();
    }, [propertyId]);

    /* ---------------- SEARCH ---------------- */
    useEffect(() => {
        if (!searchText) {
            setFiltered(damages);
        } else {
            setFiltered(
                damages.filter((d) =>
                    d.customers?.custname
                        ?.toLowerCase()
                        .includes(searchText.toLowerCase())
                )
            );
        }
    }, [searchText, damages]);

    return (
        <Box sx={{ bgcolor: colors.darkGreen, minHeight: "100vh", color: colors.vanilla, p: { xs: 2, md: 1 } }}>
            <div className="container-fluid p-0">
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <Typography variant="h5" fontWeight={800} sx={{ color: colors.vanilla }}>
                            Damage Notes
                        </Typography>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12">
                        <Paper sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                            <div className="row mb-3">
                                <div className="col-sm-4">
                                    <TextField
                                        label="Search Customer"
                                        size="small"
                                        fullWidth
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{
                                            style: { color: colors.vanilla }
                                        }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                            </div>

                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                        <TableRow>
                                            <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr No</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Customer Name</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Mobile</TableCell>
                                            <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Damage Amount</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Damage Note</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {filtered.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                                    No Records Found
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {filtered.map((row, index) => (
                                            <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                                <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{index + 1}</TableCell>
                                                <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{row.customers?.custname}</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{row.customers?.mobile}</TableCell>
                                                <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                    ₹ {Number(row.damageamount).toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{row.damagenote}</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                    {row.createdAt
                                                        ? moment(row.createdAt.toDate()).format("DD/MM/YYYY")
                                                        : "-"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </div>
                </div>
            </div>
        </Box>
    );
}

export default Damagenotes;